// Step 5d Phase 4 — offline auth gate. Stands in front of the cached app: when the device
// is enrolled but LOCKED (fresh load, explicit lock, or >7-day expiry), the cached UI is
// not shown until the PIN unwraps the DEK. A freshly-authenticated device that isn't yet
// enrolled is sent through PIN enrolment (online).
//
// CARDINAL RULE: this gate only governs LOCAL cache access. It never authenticates to the
// server — the user is already server-authenticated (useAuth) before they reach it; the
// PIN just unwraps local ciphertext.

import { useCallback, useEffect, useState } from 'react';
import { useAuth, type AuthUser } from '../context/AuthContext';
import { subscribeDek, isUnlocked } from '../lib/offline/dekSession';
import { enrollProfile, unlockProfile, unlockStateFor, getProfile, listProfiles, isOfflineExpired } from '../lib/offline/profiles';
import type { ProfileRecord } from '../lib/offline/db';
import { getDeviceId } from '../lib/offline/opQueue';
import { readSessionUser, saveSessionUser } from '../lib/offline/cache';
import { serverEnrollDevice } from '../lib/devices';

type Mode = 'loading' | 'enrol' | 'unlock' | 'pass';

const wrap: React.CSSProperties = { minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'system-ui, sans-serif' };
const card: React.CSSProperties = { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 360, boxShadow: '0 16px 48px rgba(0,0,0,0.3)' };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: 18, letterSpacing: '0.3em', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 8, boxSizing: 'border-box', marginTop: 10 };
const btn: React.CSSProperties = { width: '100%', marginTop: 14, padding: '10px', fontSize: 14, fontWeight: 700, color: '#fff', background: '#2563eb', border: 'none', borderRadius: 8, cursor: 'pointer' };

export default function OfflineAuthGate({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isInitializing, restoreUnavailable, adoptOfflineUser, logout } = useAuth();
  const [mode, setMode] = useState<Mode>('loading');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const [terminalLocked, setTerminalLocked] = useState(false);
  const [profiles, setProfiles] = useState<ProfileRecord[]>([]);
  // Shared tablet: which enrolled profile is being unlocked (defaults to the authed user).
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  // Autofill suppression: PIN fields stay readOnly until the user's first genuine gesture
  // (pointer or digit key), so password managers skip them during the on-load autofill pass.
  // autoFocus fires focus programmatically at mount, so focus must NOT arm this.
  const [fieldsArmed, setFieldsArmed] = useState(false);
  const [, force] = useState(0);

  useEffect(() => subscribeDek(() => force((n) => n + 1)), []);

  const evaluate = useCallback(async () => {
    if (isInitializing) { setMode('loading'); return; }
    if (isUnlocked() && isAuthenticated) {
      // Unlocked + authenticated: guarantee the encrypted session profile is persisted now
      // that the DEK exists, so a later OFFLINE reload can restore this user after unlock.
      if (user) void saveSessionUser(user);
      setMode('pass');
      return;
    }
    if (!isAuthenticated || !user) {
      // Not authenticated in memory. If the mount-time restore FAILED OFFLINE and this
      // device is enrolled, the user can't be restored from the (encrypted) session until
      // they unlock — so present the UNLOCK gate as the offline entry point. Otherwise the
      // login flow owns the screen.
      if (restoreUnavailable) {
        const all = await listProfiles();
        if (all.length > 0) {
          setProfiles(all);
          const target = targetUserId ?? all[0].user_id;
          setTargetUserId(target);
          setTerminalLocked((await unlockStateFor(target)).terminalLocked);
          setExpired(await isOfflineExpired());
          setMode('unlock');
          return;
        }
      }
      setMode('pass');
      return;
    }
    // Authenticated (online) — enrol if no profile, else require unlock for the cache DEK.
    if (isUnlocked()) { setMode('pass'); return; }
    const profile = await getProfile(user.id);
    if (!profile) { setMode('enrol'); return; }
    const all = await listProfiles();
    setProfiles(all);
    const target = targetUserId ?? user.id;
    setTargetUserId(target);
    setTerminalLocked((await unlockStateFor(target)).terminalLocked);
    setExpired(await isOfflineExpired());
    setMode('unlock');
  }, [isAuthenticated, user, isInitializing, restoreUnavailable, targetUserId]);

  useEffect(() => { void evaluate(); }, [evaluate]);
  // Re-evaluate when the DEK state flips (unlock/lock).
  useEffect(() => subscribeDek(() => { void evaluate(); }), [evaluate]);

  if (mode === 'loading') return <div style={wrap}><div style={{ color: '#94a3b8' }}>Loading…</div></div>;
  if (mode === 'pass') return <>{children}</>;

  async function onEnrol() {
    setError(null);
    if (pin !== pin2) { setError('PINs do not match.'); return; }
    setBusy(true);
    const r = await enrollProfile({ id: user!.id, preferred_name: user!.preferred_name, full_name: user!.full_name, email: user!.email }, pin);
    if (r.ok) {
      await serverEnrollDevice(getDeviceId()); // register with the server registry (online) so it can be revoked
      // The DEK now exists — persist the (encrypted) session profile so a later OFFLINE
      // reload can restore this user after unlock. (At login time there was no DEK yet, so
      // AuthContext's saveSessionUser no-op'd; this is the first chance to write it.)
      await saveSessionUser(user!);
    }
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    setPin(''); setPin2(''); setFieldsArmed(false); void evaluate();
  }

  async function onUnlock() {
    setError(null);
    setBusy(true);
    const r = await unlockProfile(targetUserId ?? user?.id ?? '', pin);
    setBusy(false);
    setPin('');
    setFieldsArmed(false);
    if (!r.ok) { setError(r.error); setTerminalLocked(r.state.terminalLocked); return; }
    if (!isAuthenticated) {
      // Offline entry: the DEK now lets us decrypt the last-known session profile → adopt
      // it so the cached board renders. (Token stays null; first online request heals.)
      const cached = await readSessionUser();
      if (cached) adoptOfflineUser(cached as AuthUser);
    } else if (user) {
      // Online unlock: ensure the session profile is persisted now that the DEK exists, so
      // future OFFLINE reloads can restore this user.
      await saveSessionUser(user);
    }
    void evaluate();
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
          {mode === 'enrol' ? 'Set your PIN' : 'Unlock AvSuite'}
        </div>
        {/* PR-000042 reframe: enrolment reads as the user's ONE personal PIN (reused on every
            device), not a fresh per-device secret. UX wording only — the PIN model is unchanged
            (local per device, never sent to the server). */}
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
          {mode === 'enrol'
            ? 'This is your personal PIN for AvSuite (at least 6 digits). Use the same PIN whenever you sign in on a new device. It lets you keep working when you are offline.'
            : (() => {
                const who = user ? (user.preferred_name || user.full_name || user.email)
                  : (profiles.find((p) => p.user_id === targetUserId)?.display_name ?? 'this device');
                return `Enter your PIN (${who}).`;
              })()}
        </div>

        {(terminalLocked || expired) && mode === 'unlock' && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 10px', fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
            {terminalLocked
              ? 'This device is locked after too many PIN attempts. Reconnect and sign in online to restore it.'
              : 'Your offline session has expired (7 days). Reconnect and sign in online to continue.'}
          </div>
        )}

        {/* Shared tablet: choose which enrolled profile to unlock (>1 profile). All wrap the
            same device DEK, so any one's PIN decrypts the shared cache (v2 §7). */}
        {mode === 'unlock' && profiles.length > 1 && !(terminalLocked || expired) && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>UNLOCK AS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {profiles.map((p) => (
                <button key={p.user_id} onClick={() => { setTargetUserId(p.user_id); setPin(''); setFieldsArmed(false); setError(null); void unlockStateFor(p.user_id).then((s) => setTerminalLocked(s.terminalLocked)); }}
                  style={{ fontSize: 11, padding: '4px 9px', borderRadius: 6, cursor: 'pointer', border: '1px solid #cbd5e1', background: (targetUserId ?? user?.id) === p.user_id ? '#dbeafe' : '#fff', color: '#0f172a', fontWeight: (targetUserId ?? user?.id) === p.user_id ? 700 : 400 }}>
                  {p.display_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!(terminalLocked || expired) && (
          <>
            <input style={input} type="password" inputMode="numeric" pattern="\d*" maxLength={12}
              name="offline-pin" id="offline-pin" autoComplete="one-time-code"
              data-1p-ignore="true" data-lpignore="true" data-bwignore="true"
              readOnly={!fieldsArmed} onPointerDown={() => setFieldsArmed(true)}
              placeholder="PIN" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                // readOnly swallows the arming keystroke, so apply the first digit by hand.
                if (!fieldsArmed && /^\d$/.test(e.key)) { e.preventDefault(); setFieldsArmed(true); setPin((p) => (p + e.key).slice(0, 12)); return; }
                if (e.key === 'Enter' && mode === 'unlock') void onUnlock();
              }} autoFocus />
            {mode === 'enrol' && (
              <input style={input} type="password" inputMode="numeric" pattern="\d*" maxLength={12}
                name="offline-pin-confirm" id="offline-pin-confirm" autoComplete="one-time-code"
                data-1p-ignore="true" data-lpignore="true" data-bwignore="true"
                readOnly={!fieldsArmed} onPointerDown={() => setFieldsArmed(true)}
                onKeyDown={(e) => {
                  if (!fieldsArmed && /^\d$/.test(e.key)) { e.preventDefault(); setFieldsArmed(true); setPin2((p) => (p + e.key).slice(0, 12)); }
                }}
                placeholder="Confirm PIN" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, ''))} />
            )}
            <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy}
              onClick={() => (mode === 'enrol' ? onEnrol() : onUnlock())}>
              {busy ? 'Working…' : mode === 'enrol' ? 'Set PIN & continue' : 'Unlock'}
            </button>
          </>
        )}

        {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <button onClick={() => { void logout(); }} style={{ width: '100%', marginTop: 10, padding: '8px', fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
